/* eslint-disable react/jsx-indent */
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { Box, CircularProgress, Rating, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Api from "api";
import octa from "assets/images/calm.svg";
import CustomButton from "components/customButton";
import Panel from "components/missions/Panel";
import { useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";

const StyledRating = styled(Rating)({
	"& .MuiRating-iconFilled": {
		color: "#FBD37F"
	},
	"& .MuiRating-iconHover": {
		color: "#FBD37F"
	}
});

export interface FeedbackProps
{
	missionNumber: number;
	name: string | undefined;
	clearStorage?(): void;
	nextStep?(): void;
}

function FeedbackCreate(
	{
		missionNumber,
		name,
		clearStorage,
		nextStep
	}: FeedbackProps
)
{
	const navigate = useNavigate();
	const [ rating, setRating ] = useState<number>(0);
	const [ feedback, setFeedback ] = useState<string>("");
	const [ feedbackElevenMission, setFeedbackElevenMission ] = useState<string>("");
	const [ err, setErr ] = useState<boolean>(false);
	const [ isLoading, setIsLoading ] = useState<boolean>(false);
	const [ ratingError, setRatingError ] = useState(false);
	const [ feedbackError, setFeedbackError ] = useState(false);
	const [ feedbackElevenError, setFeedbackElevenError ] = useState(false);

	function submit()
	{
		const isMissionEleven = missionNumber === 11;

		const data = isMissionEleven
			? {
				missionNumber,
				rating,
				feedbackShift: feedback || "",
				feedbackHelpful: feedbackElevenMission || ""
			}
			: {
				missionNumber,
				rating,
				feedback
			};

		const isRatingValid = !isMissionEleven || rating > 0;
		const isFeedbackValid = !isMissionEleven || feedback.length >= 30;
		const isFeedbackElevenValid = !isMissionEleven || feedbackElevenMission.length >= 30;

		setRatingError(!isRatingValid);
		setFeedbackError(!isFeedbackValid);
		setFeedbackElevenError(!isFeedbackElevenValid);

		if (!isRatingValid || !isFeedbackValid || !isFeedbackElevenValid)
		{
			return;
		}

		if (feedback.length === 0)
		{
			setErr(true);

			return;
		}

		setIsLoading(true);
		Api.feedback
			.create(data)
			.then(() =>
			{
				clearStorage && clearStorage();
				setIsLoading(false);

				if (missionNumber === 2 || missionNumber === 11 || missionNumber === 10)
				{
					nextStep && nextStep();
				}
				else if (missionNumber === 10)
				{
					setTimeout(() => navigate("/mission-eleven"), 0);
				}
				else
				{
					setTimeout(() => navigate("/dashboard"), 0);
				}
			})
			.catch(() =>
			{
				setIsLoading(false);
			});
	}

	// Render
	return (
		<Box sx={{
			height: "100vh",
			background: "rgba(78, 181, 56, .06)",
			backdropFilter: "blur(40px)",
			width: "100%",
			dsiplay: "flex",
			flexDirection: "column",
			justifyContent: "center"
		}}>
			<Panel sx={{
				height: missionNumber === 11 ? "auto" : { xl: "585px", lg: "585px", xs: "456px" },
				width: { xl: "642px", lg: "642px", sm: "442px", xs: "100%" },
				margin: "0 auto",
				padding: "24px 32px 32px 32px",
				display: "grid",
				gridTemplateRows: "auto 1fr",
				gap: "24px",
				background: "linear-gradient(103.55deg, rgba(207, 225, 255, 0.55) 9.99%, rgba(223, 223, 223, 0.13) 91.61%) !important",
				boxShadow: "none !important",
				mt: "150px",
				position: "relative"
			}}>
				<Box
					component="img"
					src={octa}
					sx={{
						width: { xs: "140px", lg: "186px" },
						transform: "rotate(-5.6deg)",
						position: "absolute",
						top: { xs: "-52px", lg: "-73px" },
						right: { xs: "-93px", lg: "-125px" }
					}} />
				<Box sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center"
				}}>
					<Box sx={{
						color: missionNumber === 11 ? "#fff" : "primary.main",
						font: { xl: "700 40px/51px Lora-Bold", lg: "700 32px/40px Lora-Bold", xs: "700 32px/23px Lora-Bold" },
						textAlign: "center"
					}}>
						{missionNumber !== 11 ? "Rate this mission" : "How was your Quest?"}
					</Box>
					{missionNumber !== 11 &&
						<Box sx={{
							color: missionNumber === 11 ? "#fff" : "primary.main",
							font: { xl: "400 22px/29px Open Sans", xs: "400 18px/23px Open Sans" },
							textAlign: "center",
							mt: 4,
							mb: 2
						}}>
							{`${ name }, your opinion matters!`}<br />
							Share your feedback to help us improve.
						</Box>
					}
					<StyledRating
						value={rating}
						onChange={(event, newValue) =>
						{
							if (newValue)
							{
								setRating(newValue);
								setRatingError(false);
							}
						}}
						name="customized-color"
						icon={<StarRoundedIcon sx={{ color: "#FBD37F", fontSize: { xs: "60px", lg: "100px" } }} />}
						emptyIcon={<StarRoundedIcon sx={{ color: "rgba(62, 128, 157, 0.3)", fontSize: { xs: "60px", lg: "100px" } }} />}
					/>
					{ratingError ? <Box sx={{ width: "100%" }}><Typography sx={{ color: missionNumber === 11 ? "#fff" : "red", mt: 1 }}>Please select a rating</Typography></Box> : null}
					<TextField
						value={feedback}
						fullWidth
						autoFocus
						inputProps={{ maxLength: 150 }}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
						{
							setFeedback(e.target.value);
							if (e.target.value.length >= 30) setFeedbackError(false);
						}}
						helperText={<Typography sx={{ color: missionNumber === 11 ? "#fff" : "#4D4D4D", font: "400 14px/19px 'Open Sans'", mt: 0, opacity: 0.5 }}>{`${ feedback.length }/150 characters`}</Typography>}
						placeholder={missionNumber === 11 ? "What changed in how you see your future? (One line is perfect)" : "Share your feedback here"}
						multiline
						minRows={missionNumber === 11 ? 3 : 6}
						maxRows={missionNumber === 11 ? 3 : 6}
						sx={{
							"& .MuiInputBase-root fieldset": {
								border: "none"
							},
							"& .MuiOutlinedInput-root": {
								font: { xs: '400 16px/24px "Open Sans"', lg: '400 22px/30px "Open Sans"' },
								color: "#4D4D4D !important",
								backgroundColor: "rgba(255, 255, 255, 0.5)",
								borderRadius: "8px"
							},
							"& .MuiFormHelperText-contained": {
								textAlign: "right",
								mt: 0
							},
							mt: 4
						}}
					/>
					{err && missionNumber !== 11 ? <Typography sx={{ color: "red" }}>Fill in this field</Typography> : null}
					{feedbackError
						?
						<Box sx={{ width: "100%" }}>
							<Typography sx={{ color: missionNumber === 11 ? "#fff" : "red", mt: 1, textAlign: missionNumber === 11 ? "left" : "cneter" }}>
								Feedback must be at least 30 characters
							</Typography>
						</Box>
						: null}
					{missionNumber === 11 &&
						<Box sx={{ width: "100%" }}>
							<TextField
								value={feedbackElevenMission}
								fullWidth
								autoFocus
								inputProps={{ maxLength: 150 }}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
								{
									setFeedbackElevenMission(e.target.value);
									if (e.target.value.length >= 30) setFeedbackElevenError(false);
								}}
								helperText={<Typography sx={{ color: missionNumber === 11 ? "#fff" : "#4D4D4D", font: "400 14px/19px 'Open Sans'", mt: 0, opacity: 0.5 }}>{`${ feedbackElevenMission.length }/150 characters`}</Typography>}
								placeholder="What part helped you most — and why? (We read every word ❤️)"
								multiline
								minRows={missionNumber === 11 ? 3 : 6}
								maxRows={missionNumber === 11 ? 3 : 6}
								sx={{
									"& .MuiInputBase-root fieldset": {
										border: "none"
									},
									"& .MuiOutlinedInput-root": {
										font: { xs: '400 16px/24px "Open Sans"', lg: '400 22px/30px "Open Sans"' },
										color: "#4D4D4D !important",
										backgroundColor: "rgba(255, 255, 255, 0.5)",
										borderRadius: "8px"
									},
									"& .MuiFormHelperText-contained": {
										textAlign: "right",
										mt: 0
									},
									mt: 1
								}}
							/>
							{feedbackElevenError
								?
								<Typography sx={{ color: missionNumber === 11 ? "#fff" : "red", mt: 1, textAlign: missionNumber === 11 ? "left" : "cneter" }}>
									This field must be at least 30 characters
								</Typography>
								: null}
						</Box>}
				</Box>
			</Panel>
			<Box sx={{ display: "flex", justifyContent: "center", mt: "64px" }}>
				{isLoading
					? <CircularProgress />
					:
					missionNumber !== 11
						?
						(
							<CustomButton.Contained
								size="large"
								onClick={submit}
							>
								Continue
							</CustomButton.Contained>
						)
						:
						(
							<CustomButton.Contained
								size="large"
								onClick={submit}
								sx={{ background: "#fff", color: "primary.main" }}
							>
								Continue
							</CustomButton.Contained>
						)
				}
			</Box>

		</Box>
	);
}

export default FeedbackCreate;
